import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import OverviewRevenueCard from "./OverviewRevenueCard";
import MetricCard from "./MetricCard";
import NoDataFound from "../../../components/common/NoDataFound";
import RevenueChart from "./RevenueChart";
import GenderCard from "./GenderRow/GenderCard";
import CustomerCard from "./GenderRow/CustomerCard";
import AgeRangeCard from "./GenderRow/AgeRangeCard";
import CommonBookingTimes from "./CommonBookingTimes";
import OccupancyAndSales from "./OccupancyAndSales";
import BranchDropdown from "./BranchDropdown";
import { useDispatch, useSelector } from "react-redux";
import { getBranchListWithCity } from "store/actions/branchSlice";
import DateRangePicker from "components/common/DateRangePicker";
import { getMinMaxDate } from "components/common/Utils";
import {
  getOverviewDashboardChartDetails,
  getOverviewDashboardDetails,
} from "store/actions/dashboardSlice";
import IsLoading from "components/common/IsLoading";
import RevenueOverview from "./OverviewCards/RevenueOverview";
import Cashback from "./OverviewCards/Cashback";
import BookingPerformance from "./OverviewCards/BookingPerformance";
import UserActivity from "./OverviewCards/UserActivity";
import Coupons from "./OverviewCards/Coupons";
import { ShimmerSimpleGallery } from "react-shimmer-effects";
import OverviewDashboardShimmer from "components/ShimmerEffects/OverviewDashboardShimmer";
import Switch from "components/common/Switch";
import { SuccessIcon } from "components/common/SVGIcons";

const Overview = () => {
  const { t } = useTranslation();
  document.title = t("Overview Dashboard | Work Space");

  const dispatch = useDispatch();

  const { branchesWithCity } = useSelector((state) => state.Branches);
  const { loading, overviewDashboard } = useSelector(
    (state) => state.Dashboards
  );

  const [dates, setDates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [hasActualBookingDateChosen, setHasActualBookingDateChosen] =
    useState(false);

  const [metricsData, setMetricsData] = useState([]);
  useEffect(() => {
    dispatch(getBranchListWithCity());
    getAllData();
  }, []);

  const getAllData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD

    const isReset = actionType === "RESET";
    const payload = {
      spaceTypeInt: [], // [] for all , 1 - workspace, 2- meetingroom, 3 - workshop
      hasActualBookingDateChosen: isReset ? false : hasActualBookingDateChosen,
      branches: isReset ? [] : branches,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getOverviewDashboardDetails(payload));

    // FOR CHART DATA:
    // dispatch(getOverviewDashboardChartDetails(payload));
  };

  const handleReset = () => {
    setHasActualBookingDateChosen(false);
    setDates([]);
    setBranches([]);
    getAllData({ actionType: "RESET" });
  };

  useEffect(() => {
    setMetricsData([
      {
        id: 1,
        key: "totalRevenue",
        title: "Total Revenue",
        value: overviewDashboard?.customers?.totalRevenue,
        unit: "SR",
        trend: `${
          overviewDashboard?.customers?.totalRevenueTrendPercentage ?? ""
        }%`,
        trendUp: overviewDashboard?.customers?.totalRevenueTrendPositive,
        iconName: null,
      },
      {
        id: 2,
        key: "totalBookings",
        title: "Total Number of Bookings",
        value: overviewDashboard?.customers?.totalBookings,
        unit: "",
        trend: null,
        iconName: null,
      },
      {
        id: 3,
        key: "redemptionRate",
        title: "Redemption Rate",
        value: overviewDashboard?.customers?.redemptionRate,
        unit: "%",
        trend: null,
        iconName: null,
      },
      {
        id: 4,
        key: "retentionRate",
        title: "Retention Rate",
        value: overviewDashboard?.customers?.repeatUsersPercentage,
        unit: "%",
        trend: null,
        iconName: null,
      },
      {
        id: 5,
        key: "occupancyRate",
        title: "Total Occupancy Rate",
        value: overviewDashboard?.customers?.overallOccupancyRate,
        unit: "%",
        subtext: `Expected revenue at 100% occupancy: ${
          overviewDashboard?.customers?.expectedRevenue ?? ""
        }`,
        trend: null,
        iconName: null,
      },
      {
        id: 6,
        key: "averageBookings",
        title: "Average Booking",
        value: overviewDashboard?.customers?.averageBookingsPerDay,
        unit: "",
        trend: null,
        iconName: null,
      },
      {
        id: 7,
        key: "customerLifeTimeValue",
        title: "Customer Life Time Value",
        value: overviewDashboard?.customers?.cltv,
        unit: "SR",
        trend: null,
        iconName: null,
      },
      {
        id: 8,
        key: "cashbackReceived",
        title: "Total Cashback",
        value: overviewDashboard?.customers?.totalCashbackAmount,
        unit: "SR",
        trend: null,
        iconName: null,
      },
      {
        id: 9,
        key: "cashbackUsed",
        title: "Used Cashback",
        value: overviewDashboard?.customers?.usedCashbacPercentage,
        unit: "%",
        secondaryValue: overviewDashboard?.customers?.usedCashbackAmount,
        secondaryUnit: "SR",
        trend: null,
        iconName: null,
      },
      {
        id: 10,
        key: "usedCoupons",
        title: "Used Coupons",
        value: overviewDashboard?.customers?.usedCoupons,
        unit: "",
        trend: null,
        iconName: "coupon",
      },
      {
        id: 11,
        key: "totalUsers",
        title: "Number User",
        value: overviewDashboard?.customers?.totalUsers,
        subtext: `Active Users ${
          overviewDashboard?.customers?.totalActiveUsers ?? ""
        }`,
        unit: "",
        trend: null,
        iconName: "user",
      },
      {
        id: 12,
        key: "newUsersByRefferalCode",
        title: "New User by Referral Code",
        value: overviewDashboard?.customers?.newReferralUsers,
        subtext: "",
        unit: "",
        trend: null,
        iconName: "user",
      },
      {
        id: 13,
        key: "cancelledBookings",
        title: "Cancelled Bookings",
        value: `${overviewDashboard?.customers?.cancelledBookingCount ?? 0} (${
          overviewDashboard?.customers?.cancelledBookingPercentage ?? 0
        })%`,
        unit: "",
        trend: null,
        iconName: null,
      },
      {
        id: 14,
        key: "completedBookings",
        title: "Completed Bookings",
        value: `${overviewDashboard?.customers?.completedBookingCount ?? 0} (${
          overviewDashboard?.customers?.completedBookingPercentage ?? 0
        })%`,
        unit: "",
        trend: null,
        iconName: null,
      },
    ]);
  }, [overviewDashboard]);

  const [selectedRevenue, setSelectedRevenue] = useState({});

  useEffect(() => {
    setSelectedRevenue(metricsData[0]);
  }, [overviewDashboard, metricsData]);

  const handleSelectionChange = (newSelected) => {
    setBranches(newSelected);
  };

  const handleBranchApply = () => {
    getAllData({ actionType: "SEARCH" });
  };

  return (
    <React.Fragment>
      <div className="page-content overview_dashboard">
        <Container fluid>
          {/* FILTER ROW */}          
          <Row>
            <div className="g_between">
              <h1 className="main_title">{t("Overview")}</h1>
              <div className="d-flex gap-4 common_filter">
                <div className="buttons">
                  <button
                    className="search_btn"
                    onClick={() => getAllData({ actionType: "SEARCH" })}
                  >
                    {t("Search")}
                  </button>
                  <button className="reset_btn" onClick={handleReset}>
                    {t("Reset")}
                  </button>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Switch
                    checked={hasActualBookingDateChosen}
                    handleOnChange={() =>
                      setHasActualBookingDateChosen(!hasActualBookingDateChosen)
                    }
                  />
                  <label
                    htmlFor="hasActualBookingDateChosen"
                    className="fs_14"
                    style={{ color: "#6b7280" }}
                  >
                    {t("Filter by Actual Booking Date")}{" "}
                  </label>
                </div>
                <BranchDropdown
                  data={branchesWithCity}
                  label={t("Branch")}
                  allLabel={t("Choose All Branches")}
                  resetLabel={t("Reset all")}
                  onSelectionChange={handleSelectionChange}
                  selectedData={branches}
                  handleApply={handleBranchApply}
                />
                <DateRangePicker dates={dates} setDates={setDates} />
              </div>
            </div>
          </Row>
          <Row className="revenue_row">
            <h2 className="main_title">{t("Revenue Overview")}</h2>
            <div className={`second_div ${loading ? "" : "pt-2"} `}>
              {loading ? (
                <OverviewDashboardShimmer />
              ) : (
                <Row className="overview_row">
                  <RevenueOverview />
                </Row>
              )}
            </div>
          </Row>
          {/* REVENUE ROW */}
          <Row className="revenue_row">
            <h2 className="main_title mb-2">{t("Revenue Details")}</h2>
            {loading ? (
              <OverviewDashboardShimmer />
            ) : overviewDashboard?.spaceTypeStats?.length > 0 ? (
              <OverviewRevenueCard />
            ) : (
              <NoDataFound text={t("No revenue details found")} />
            )}
          </Row>
          <h2 className="main_title mt-3">{t("General Details")}</h2>
          <div className="second_div pt-2">
            {/* SECOND ROW */}
            {loading ? null : (
              <Row className="overview_row">
                <Coupons />
                <Cashback />
                <BookingPerformance />
                <UserActivity />
              </Row>
            )}

            {/* REVENUE ROW */}
            <Row className="common_row">
              <RevenueChart selectedRevenue={selectedRevenue} />
            </Row>

            {/* BOOKING TIMES ROW */}
            <Row className="common_row booking_time_row">
              <CommonBookingTimes />
            </Row>

            {/* OCCUPANCY RATE AND TOTAL SALE */}
            <Row className="common_row">
              <OccupancyAndSales />
            </Row>

            {/* PIE CHARTS */}
            {loading ? null : (
              <Row className="gender_row mt_15">
                <GenderCard
                  genderStats={overviewDashboard?.customers?.genderStats}
                />
                <CustomerCard
                  roleTypeStats={overviewDashboard?.customers?.roleTypeStats}
                />
                <AgeRangeCard
                  ageGroups={overviewDashboard?.customers?.genderAgeGroups}
                />
              </Row>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Overview;
